import classnames from "classnames";
import React from "react";
import BreadcrumbPill from "web/react/components/breadcrumb-pill/breadcrumb-pill";
import { AllSelectedFilterInfo, FilterClickHandler } from "web/types/feed-filters";
import { UniversalFilterOption } from "web/types/universal-filters";
import { getSelectedOptions } from "web/react/components/feeds-filters/hooks/use-filter-manager";
import { gettext } from "web/script/modules/django-i18n";
import styles from "./feeds-filters-breadcrumb-pills.css";

interface BreadcrumbPillProps {
    selectedFilterInfo: AllSelectedFilterInfo;
    onClick: FilterClickHandler;
    className?: string;
}

/**
 * Adds `gender All` item to the `section-gender` breadcrumbs manually.
 *
 * Basically, `useSelectedFilterInfo` hook is responsible for building the breadcrumbs' options.
 * Its algorithm skips the default checked options, i.e. in the current case it will skip `gender All` option.
 * We can either modify the hook and the Filtering logic at all or simply adding the default option here.
 * Being part of the Filtering mechanism - this option will be counted as a checked filter and will
 * be part of all the Filtering logic and back-end requests.
 * As we simply want to visually render the `All` option, without any other complex Filtering functionality attached to it
 * we prefer the second approach of adding the option manually here.
 */
function addGenderAll(selectedFilterInfo: AllSelectedFilterInfo): AllSelectedFilterInfo {
    return {
        ...selectedFilterInfo,
        "section-gender": {
            ...selectedFilterInfo["section-gender"],
            breadcrumbs: [
                {
                    is_default: true,
                    checked: true,
                    disabled: false,
                    label: "All",
                    name: "gender",
                    type: "radio",
                    url: "#",
                    value: "all",
                },
            ],
        },
    };
}

function BreadcrumbPills({
    className,
    onClick,
    selectedFilterInfo,
}: BreadcrumbPillProps): null | JSX.Element {
    // In the case `section-gender` exists, but no certain gender option/breadcrumb is checked,
    // it means that the default gender is being checked, i.e. `gender All`.
    // Please refer to `addGenderAll` for more details.
    if (
        selectedFilterInfo["section-gender"] &&
        !selectedFilterInfo["section-gender"].breadcrumbs.length
    ) {
        selectedFilterInfo = addGenderAll(selectedFilterInfo);
    }

    const selectedOptions: UniversalFilterOption[] = getSelectedOptions(selectedFilterInfo);

    // In the case there aren't any options checked
    if (!selectedOptions.length) {
        return (
            <span className={styles.pillsEmpty}>
                {gettext("feed.breadcrumbs.no_filters.label")}
            </span>
        );
    }

    return (
        <ul className={classnames(styles.pills, className)}>
            {selectedOptions.map((option) => (
                <li className={styles.pill} key={`${option.name}=${option.value}`}>
                    <BreadcrumbPill
                        onClick={(): void => onClick(option, true)}
                        title={option.label}
                        href={option.url}
                        hideIcon={option.name === "gender" && option.value === "all"}
                    />
                </li>
            ))}
        </ul>
    );
}

export default BreadcrumbPills;
